import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/common";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize } from "lucide-react";

const LABELS = { bill_receipt: "Bill / payment receipt", selfie: "Selfie", id_front: "Government ID (front)", id_back: "Government ID (back)" };

function useBlobUrls(req) {
  const [urls, setUrls] = useState({});
  useEffect(() => {
    const made = [];
    (req.document_files || []).forEach((d) => {
      api.get(`/admin/link-requests/${req.id}/documents/${d.id}`, { responseType: "blob" })
        .then((r) => { const u = URL.createObjectURL(r.data); made.push(u); setUrls((p) => ({ ...p, [d.id]: u })); }).catch(() => {});
    });
    return () => made.forEach((u) => URL.revokeObjectURL(u));
  }, [req]);
  return urls;
}

function Viewer({ items, index, setIndex }) {
  const [zoom, setZoom] = useState(1);
  const it = items[index];
  return (
    <Dialog open onOpenChange={(o) => !o && setIndex(null)}>
      <DialogContent className="max-w-5xl" data-testid="image-viewer">
        <DialogHeader><DialogTitle>{LABELS[it.kind]}</DialogTitle></DialogHeader>
        <div className="overflow-auto max-h-[70vh] bg-slate-900 rounded flex items-center justify-center">
          <img src={it.url} alt={LABELS[it.kind]} style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }} className="max-w-full transition-transform" />
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} data-testid="viewer-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => setZoom((z) => Math.min(4, z + 0.25))} data-testid="viewer-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => setZoom(1)} data-testid="viewer-fit"><Maximize className="h-4 w-4" /></Button>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={index === 0} onClick={() => { setZoom(1); setIndex(index - 1); }} data-testid="viewer-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" disabled={index === items.length - 1} onClick={() => { setZoom(1); setIndex(index + 1); }} data-testid="viewer-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button size="sm" onClick={() => setIndex(null)} data-testid="viewer-close">Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between gap-3 py-1 border-b border-slate-100 text-sm"><span className="text-slate-500">{label}</span><span className="text-right break-words min-w-0">{value || "—"}</span></div>;
}

export function LinkRequestDetails({ req, onClose, onDecide }) {
  const urls = useBlobUrls(req);
  const [view, setView] = useState(null);
  const imgs = (req.document_files || []).map((d) => ({ ...d, url: urls[d.id] })).filter((d) => d.url);
  const v = req.verification || {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="link-details-modal">
        <DialogHeader><DialogTitle>Account Linking Request Details</DialogTitle></DialogHeader>
        {req.identity_check_required && <p className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2" data-testid="first-link-flag">First-time link: identity check required</p>}
        <div className="grid md:grid-cols-2 gap-6">
          <div><p className="text-xs uppercase text-slate-500 mb-1">Consumer</p>
            <Row label="Name" value={req.consumer_name} /><Row label="Email / username" value={req.consumer_email} /><Row label="Mobile" value={req.consumer_mobile} />
            <Row label="Account type" value={req.account_type_label} /><Row label="Registered" value={req.registered_at && fmtDate(req.registered_at)} /></div>
          <div><p className="text-xs uppercase text-slate-500 mb-1">Subscriber account</p>
            <Row label="Account number" value={req.account_number} /><Row label="Account name (submitted)" value={req.account_name} />
            <Row label="On file" value={req.account_on_file ? `${req.account_on_file.name} (${req.account_on_file.status})` : "Not found"} />
            <Row label="Relationship" value={req.relationship} /></div>
          <div className="md:col-span-2"><p className="text-xs uppercase text-slate-500 mb-1">Submitted</p>
            <Row label="Reference" value={req.reference} /><Row label="Date submitted" value={req.submitted && fmtDate(req.submitted)} />
            <Row label="Status" value={<StatusBadge status={req.status} />} /><Row label="Latest bill amount" value={v.latest_bill_amount} /><Row label="Notes" value={v.notes} />
            {req.review_note && <Row label="Review note" value={req.review_note} />}</div>
        </div>
        <div><p className="text-xs uppercase text-slate-500 mb-2">Photos</p>
          {(req.document_files || []).length === 0 ? <p className="text-sm text-slate-500" data-testid="no-photos">No photos were uploaded with this request.</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{req.document_files.map((d) => {
              const i = imgs.findIndex((x) => x.id === d.id);
              return (<button key={d.id} onClick={() => i >= 0 && setView(i)} className="text-left" data-testid={`photo-${d.kind}`}>
                {urls[d.id] ? <img src={urls[d.id]} alt={LABELS[d.kind]} className="h-28 w-full object-cover rounded border" /> : <div className="h-28 rounded border bg-slate-50 grid place-items-center text-xs text-slate-400">Loading…</div>}
                <p className="text-xs mt-1">{LABELS[d.kind]}</p></button>);
            })}</div>)}
          {req.document_files?.length > 0 && !req.document_files.some((d) => d.kind === "bill_receipt") && <p className="text-xs text-rose-700 mt-2">No receipt uploaded.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="link-details-close">Close</Button>
          {req.status === "pending" && <>
            <Button variant="outline" className="text-rose-700" onClick={() => onDecide(false)} data-testid="link-details-reject">Reject</Button>
            <Button disabled={!req.account_on_file} onClick={() => onDecide(true)} data-testid="link-details-approve">Approve</Button></>}
        </DialogFooter>
        {view !== null && imgs[view] && <Viewer items={imgs} index={view} setIndex={setView} />}
      </DialogContent>
    </Dialog>
  );
}

export const _errMsg = errMsg;
